import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";

import { renewSession, tokens, webSocketUrl } from "./api";
import { useAuth } from "./auth";
import { commandLabel } from "./commands";
import { applyEvent, resynchronize } from "./liveEvents";
import type { AudioFrame, LiveEvent, ScreenFrame } from "./types";

export type LiveStatus = "connecting" | "live" | "reconnecting" | "offline";

type FrameHandler = (frame: ScreenFrame) => void;
type AudioFrameHandler = (frame: AudioFrame) => void;

interface Live {
  status: LiveStatus;
  /** Calls [listener] for every event until the returned function is called. */
  subscribe(listener: (event: LiveEvent) => void): () => void;
  /** Asks the server for a device's screen/audio frames until the returned function is called. */
  watch(deviceId: string): () => void;
  /** Calls [handler] with every screen frame of a device until the returned function is called. */
  subscribeFrames(deviceId: string, handler: FrameHandler): () => void;
  /** Calls [handler] with every audio chunk of a device until the returned function is called. */
  subscribeAudio(deviceId: string, handler: AudioFrameHandler): () => void;
}

const LiveContext = createContext<Live>({
  status: "offline",
  subscribe: () => () => {},
  watch: () => () => {},
  subscribeFrames: () => () => {},
  subscribeAudio: () => () => {},
});

const CLOSE_UNAUTHORIZED = 4401;

/**
 * Commands whose own panel shows how they went, so a toast would only repeat it: the
 * file browser lists a folder every time a device page opens, and a viewing or a listen
 * shows its own summary when it ends. Their failures still get a toast, except listing,
 * whose panel shows the error in place.
 */
const SHOWN_IN_PLACE = new Set(["list_dir", "screen_view", "screen_stream", "audio_capture"]);
const PING_EVERY_MS = 25_000;
const MAX_BACKOFF_MS = 30_000;

/** The delay before reconnection attempt number [attempt], with jitter. */
export function reconnectDelay(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(attempt, 10));
  return base + random() * base * 0.3;
}

/**
 * Keeps a WebSocket to the server's event stream open while someone is signed in,
 * applies what arrives to the cached data, and reconnects when the connection drops.
 */
export function LiveProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LiveStatus>("offline");
  const listeners = useRef(new Set<(event: LiveEvent) => void>());
  const frameHandlers = useRef(new Map<string, Set<FrameHandler>>());
  const audioHandlers = useRef(new Map<string, Set<AudioFrameHandler>>());
  // How many parts of the screen watch each device; the server is told once per device.
  const watching = useRef(new Map<string, number>());
  // Sends a frame on the socket the server has accepted; null while there is none.
  const transmit = useRef<((frame: Record<string, unknown>) => void) | null>(null);
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!userId) {
      setStatus("offline");
      return;
    }
    let socket: WebSocket | null = null;
    let stopped = false;
    let attempt = 0;
    let wasLive = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let ping: ReturnType<typeof setInterval> | undefined;

    // Commands sent to many devices at once. Twelve devices finishing must not
    // mean twelve notifications; the broadcast page shows how they did.
    const broadcasts = new Set<string>();

    const announce = (event: LiveEvent) => {
      if (event.event === "command.batch_dispatched") {
        if (event.data.total > 1) broadcasts.add(event.data.batch_id);
      } else if (event.event === "command.updated") {
        const { command } = event.data;
        // Only the person who sent a command is told how it went.
        if (command.created_by === userId && !broadcasts.has(command.batch_id)) {
          const label = commandLabel(command.type);
          const inPlace = SHOWN_IN_PLACE.has(command.type);
          if (command.status === "completed") {
            if (!inPlace) toast.success(`${label} completed`);
          } else if (command.status === "failed" && command.type !== "list_dir") {
            toast.error(`${label} failed`, { description: command.error ?? undefined });
          }
        }
      } else if (event.event === "alert.opened" && event.data.alert.severity === "critical") {
        toast.warning(event.data.alert.message);
      }
    };

    const connect = () => {
      if (stopped) return;
      const access = tokens.get()?.access;
      if (!access) return;
      setStatus(wasLive ? "reconnecting" : "connecting");

      const current = new WebSocket(webSocketUrl("/ws/staff"));
      socket = current;
      current.onopen = () => current.send(JSON.stringify({ type: "auth", token: access }));
      current.onmessage = (message) => {
        let frame: { type?: string } & Partial<LiveEvent>;
        try {
          frame = JSON.parse(String(message.data)) as typeof frame;
        } catch {
          return;
        }
        if (frame.type === "ready") {
          attempt = 0;
          setStatus("live");
          // Whatever happened while the stream was down was missed.
          if (wasLive) resynchronize(queryClient);
          wasLive = true;
          clearInterval(ping);
          ping = setInterval(() => {
            if (current.readyState === WebSocket.OPEN) current.send('{"type":"ping"}');
          }, PING_EVERY_MS);
          transmit.current = (outgoing) => {
            if (current.readyState === WebSocket.OPEN) current.send(JSON.stringify(outgoing));
          };
          // The server forgets who watched what when a connection ends.
          for (const deviceId of watching.current.keys()) {
            transmit.current({ type: "watch", device_id: deviceId });
          }
        } else if (frame.type === "event" && frame.event === "device.screen_frame") {
          // Frames are for whoever is looking right now, never for the cache.
          const screen = frame.data;
          if (screen) frameHandlers.current.get(screen.device_id)?.forEach((handler) => handler(screen));
        } else if (frame.type === "event" && frame.event === "device.audio_frame") {
          // Audio chunks are for whoever is listening right now, never for the cache.
          const audio = frame.data;
          if (audio) audioHandlers.current.get(audio.device_id)?.forEach((handler) => handler(audio));
        } else if (frame.type === "event" && frame.event) {
          const event = frame as LiveEvent;
          applyEvent(queryClient, event);
          announce(event);
          listeners.current.forEach((listener) => listener(event));
        }
      };
      current.onclose = (closed) => {
        clearInterval(ping);
        if (socket === current) transmit.current = null;
        if (stopped || socket !== current) return;
        setStatus("reconnecting");
        const again = () => {
          retry = setTimeout(connect, reconnectDelay(attempt));
          attempt += 1;
        };
        if (closed.code === CLOSE_UNAUTHORIZED) {
          // The access token expired. A refused renewal ends the session, which
          // unmounts this provider; a failed one is retried like any other drop.
          void renewSession().then(again);
        } else {
          again();
        }
      };
      current.onerror = () => current.close();
    };

    connect();
    const online = () => {
      if (socket?.readyState !== WebSocket.OPEN) {
        clearTimeout(retry);
        attempt = 0;
        socket?.close();
        connect();
      }
    };
    window.addEventListener("online", online);

    return () => {
      stopped = true;
      transmit.current = null;
      clearTimeout(retry);
      clearInterval(ping);
      window.removeEventListener("online", online);
      socket?.close();
    };
  }, [userId, queryClient]);

  const subscribe = useCallback((listener: (event: LiveEvent) => void) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  const watch = useCallback((deviceId: string) => {
    const count = (watching.current.get(deviceId) ?? 0) + 1;
    watching.current.set(deviceId, count);
    if (count === 1) transmit.current?.({ type: "watch", device_id: deviceId });
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const left = (watching.current.get(deviceId) ?? 1) - 1;
      if (left > 0) {
        watching.current.set(deviceId, left);
        return;
      }
      watching.current.delete(deviceId);
      transmit.current?.({ type: "unwatch", device_id: deviceId });
    };
  }, []);

  const subscribeFrames = useCallback((deviceId: string, handler: FrameHandler) => {
    let handlers = frameHandlers.current.get(deviceId);
    if (!handlers) {
      handlers = new Set();
      frameHandlers.current.set(deviceId, handlers);
    }
    handlers.add(handler);
    return () => {
      handlers.delete(handler);
      if (handlers.size === 0 && frameHandlers.current.get(deviceId) === handlers) {
        frameHandlers.current.delete(deviceId);
      }
    };
  }, []);

  const subscribeAudio = useCallback((deviceId: string, handler: AudioFrameHandler) => {
    let handlers = audioHandlers.current.get(deviceId);
    if (!handlers) {
      handlers = new Set();
      audioHandlers.current.set(deviceId, handlers);
    }
    handlers.add(handler);
    return () => {
      handlers.delete(handler);
      if (handlers.size === 0 && audioHandlers.current.get(deviceId) === handlers) {
        audioHandlers.current.delete(deviceId);
      }
    };
  }, []);

  const value: Live = { status, subscribe, watch, subscribeFrames, subscribeAudio };
  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export function useLive(): Live {
  return useContext(LiveContext);
}
